export type GigPricingBreakdown = {
  posterPaymentAmountCents: number;
  teenPayoutAmountCents: number;
  platformFeeCents: number;
};

export type MultiTeenGigPricingBreakdown = {
  posterPaymentPerTeenCents: number;
  seatCount: number;
  totalPosterPaymentAmountCents: number;
  teenPayoutPerSeatCents: number;
  totalTeenPayoutAmountCents: number;
  platformFeeCents: number;
  processingFeeCents: number;
};

const TARGET_NET_PROFIT_BASIS_POINTS = 800;
const STRIPE_PROCESSING_BASIS_POINTS = 290;
const STRIPE_PROCESSING_FIXED_CENTS = 30;

export const parseUsdInputToCents = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const numericValue = Number(trimmed);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  return Math.round(numericValue * 100);
};

export const calculateGigPricingFromPosterAmountCents = (posterPaymentAmountCents: number): GigPricingBreakdown => {
  if (posterPaymentAmountCents <= 0) {
    return {
      posterPaymentAmountCents: 0,
      teenPayoutAmountCents: 0,
      platformFeeCents: 0,
    };
  }

  const stripeRate = STRIPE_PROCESSING_BASIS_POINTS / 10_000;
  const targetProfitRate = TARGET_NET_PROFIT_BASIS_POINTS / 10_000;
  const numerator = ((stripeRate + targetProfitRate) * posterPaymentAmountCents) + STRIPE_PROCESSING_FIXED_CENTS;
  const platformFeeCents = Math.round(numerator / (1 - stripeRate));
  const teenPayoutAmountCents = Math.max(0, posterPaymentAmountCents - platformFeeCents);

  return {
    posterPaymentAmountCents,
    teenPayoutAmountCents,
    platformFeeCents,
  };
};

export const calculateMultiTeenGigPricingFromPosterAmountCents = (
  posterPaymentPerTeenCents: number,
  seatCount: number,
): MultiTeenGigPricingBreakdown => {
  if (posterPaymentPerTeenCents <= 0 || seatCount <= 0) {
    return {
      posterPaymentPerTeenCents: 0,
      seatCount: Math.max(0, seatCount),
      totalPosterPaymentAmountCents: 0,
      teenPayoutPerSeatCents: 0,
      totalTeenPayoutAmountCents: 0,
      platformFeeCents: 0,
      processingFeeCents: 0,
    };
  }

  const normalizedSeatCount = Math.max(1, Math.floor(seatCount));
  const totalPosterPaymentAmountCents = posterPaymentPerTeenCents * normalizedSeatCount;
  const combinedPricing = calculateGigPricingFromPosterAmountCents(totalPosterPaymentAmountCents);

  return {
    posterPaymentPerTeenCents,
    seatCount: normalizedSeatCount,
    totalPosterPaymentAmountCents,
    teenPayoutPerSeatCents: Math.floor(combinedPricing.teenPayoutAmountCents / normalizedSeatCount),
    totalTeenPayoutAmountCents: combinedPricing.teenPayoutAmountCents,
    platformFeeCents: combinedPricing.platformFeeCents,
    processingFeeCents: 0,
  };
};

export const inferPosterPaymentAmountCentsFromTeenPayout = (teenPayoutAmountCents: number): number | null => {
  if (!Number.isInteger(teenPayoutAmountCents) || teenPayoutAmountCents <= 0) {
    return null;
  }

  let low = Math.max(100, teenPayoutAmountCents);
  let high = 1_000_000;
  let match: number | null = null;

  while (low <= high) {
    const candidate = Math.floor((low + high) / 2);
    const candidateTeenPayout = calculateGigPricingFromPosterAmountCents(candidate).teenPayoutAmountCents;

    if (candidateTeenPayout < teenPayoutAmountCents) {
      low = candidate + 1;
      continue;
    }

    if (candidateTeenPayout === teenPayoutAmountCents) {
      match = candidate;
    }
    high = candidate - 1;
  }

  return match;
};
